import { ruPlural } from '@/lib/content/declension';

/**
 * UI-тексты страницы провайдера (ModernProviderPage).
 * Динамические строки — функции; склонения и формат чисел остаются в компоненте
 * (он передаёт сюда уже посчитанные значения / слово выбирается тут).
 */
export const providerPage = {
    en: {
        // «Все X» (вертикаль-зависимая навигация назад)
        back: {
            esim: 'All eSIM providers',
            vpn: 'All VPN services',
            tours: 'All tours',
            transport: 'All transport',
            insurance: 'All insurance',
            stay: 'All accommodation',
        },
        // Основная CTA по вертикали
        cta: {
            esim: (price) => `Get eSIM${price ? ` from ${price}` : ''}`,
            vpn: 'Get VPN',
            tours: 'Book now',
            transport: 'Check availability',
            insurance: 'Get a quote',
            stay: 'See prices',
            default: 'Visit website',
        },
        // Подпись вертикали (категория)
        vlabel: { esim: 'eSIM', vpn: 'VPN', tours: 'Tours', transport: 'Transport', stay: 'Stay', insurance: 'Insurance' },
        // Регион тарифа (eSIM)
        region: { local: 'Local', regional: 'Regional', europe: 'Europe', global: 'Global' },

        // Заголовки секций / кнопки
        benefits: 'Benefits',
        audience: 'Who Is It For',
        why: 'Why Choose This Provider',
        features: 'Features',
        plans: 'Plans',
        paymentMethods: 'Payment Methods',
        promocodes: 'Promo Codes',
        tours: 'Tours',
        allTours: 'All Tours',
        languages: 'Languages',
        bestValue: 'Best value',
        howEsim: 'How to Get eSIM',
        how: 'How It Works',

        // Надзаголовки (eyebrow) секций
        eyebrow: {
            about: 'About',
            pros: 'Pros',
            capabilities: 'Capabilities',
            guide: 'Guide',
            discounts: 'Discounts',
            payment: 'Payment',
            support: 'Support',
            audience: 'Audience',
            choice: 'Choice',
            related: 'Other providers',
        },

        relatedTitle: 'Compare alternatives',
        relatedDetails: 'Details →',
        allPromoCodes: 'All promo codes',
        plansLead: 'Choose the right plan for your trip.',
        col: { data: 'Data', days: 'Days', price: 'Price', perDay: 'Per day', perGb: 'Per GB' },
        select: 'Select →',
        promo: { copy: 'Copy', copied: 'Copied', show: 'Show code', use: 'Use promo', validUntil: 'Valid until: ' },

        // Динамические заголовки
        aboutTitle: (name) => `What is ${name}`,
        plansTitle: (name) => `${name} plans`,
        toursShowcaseTitle: (name, inCountry) => `Popular ${name} tours${inCountry ? ` in ${inCountry}` : ''}`,
        toursShowcaseLead: 'Picked from the catalogue by traveller rating and review count.',
        allToursLink: (total, name) => `All ${total} tours on ${name} →`,
        stickyFrom: (price) => `from ${price}`,
        // Слова для счётчиков (число форматирует компонент)
        reviewsWord: (n) => (n === 1 ? 'review' : 'reviews'),
        toursWord: (n) => (n === 1 ? 'tour' : 'tours'),
        stepsWord: (n) => (n === 1 ? 'step' : 'steps'),
    },
    ru: {
        back: {
            esim: 'Все провайдеры eSIM',
            vpn: 'Все VPN сервисы',
            tours: 'Все туры',
            transport: 'Весь транспорт',
            insurance: 'Все страховки',
            stay: 'Все варианты жилья',
        },
        cta: {
            esim: (price) => `Купить eSIM${price ? ` от ${price}` : ''}`,
            vpn: 'Получить VPN',
            tours: 'Забронировать',
            transport: 'Проверить наличие',
            insurance: 'Рассчитать цену',
            stay: 'Смотреть цены',
            default: 'Перейти на сайт',
        },
        vlabel: { esim: 'eSIM', vpn: 'VPN', tours: 'Туры', transport: 'Транспорт', stay: 'Жильё', insurance: 'Страховка' },
        region: { local: 'Локально', regional: 'Регион', europe: 'Европа', global: 'Глобально' },

        benefits: 'Преимущества',
        audience: 'Кому подойдёт',
        why: 'Почему именно этот провайдер',
        features: 'Функции',
        plans: 'Тарифы',
        paymentMethods: 'Способы оплаты',
        promocodes: 'Промокоды',
        tours: 'Туры',
        allTours: 'Все туры',
        languages: 'Языки',
        bestValue: 'Выгодно',
        howEsim: 'Как получить eSIM',
        how: 'Как это работает',

        eyebrow: {
            about: 'О провайдере',
            pros: 'Плюсы',
            capabilities: 'Возможности',
            guide: 'Инструкция',
            discounts: 'Скидки',
            payment: 'Оплата',
            support: 'Поддержка',
            audience: 'Аудитория',
            choice: 'Выбор',
            related: 'Другие провайдеры',
        },

        relatedTitle: 'Сравните альтернативы',
        relatedDetails: 'Подробнее →',
        allPromoCodes: 'Все промокоды',
        plansLead: 'Выберите подходящий тариф для вашей поездки.',
        col: { data: 'Данные', days: 'Срок', price: 'Цена', perDay: 'За день', perGb: 'За ГБ' },
        select: 'Выбрать →',
        promo: { copy: 'Копировать', copied: 'Скопировано', show: 'Показать код', use: 'Использовать', validUntil: 'Действует до: ' },

        aboutTitle: (name) => `Что такое ${name}`,
        plansTitle: (name) => `Тарифы ${name}`,
        toursShowcaseTitle: (name, inCountry) => `Популярные туры ${name}${inCountry ? ` в ${inCountry}` : ''}`,
        toursShowcaseLead: 'Подборка из каталога — по рейтингу и количеству отзывов путешественников.',
        allToursLink: (total, name) => `Все ${total} ${ruPlural(total, ['тур', 'тура', 'туров'])} на ${name} →`,
        stickyFrom: (price) => `от ${price}`,
        reviewsWord: (n) => ruPlural(n, ['отзыв', 'отзыва', 'отзывов']),
        toursWord: (n) => ruPlural(n, ['тур', 'тура', 'туров']),
        stepsWord: (n) => ruPlural(n, ['шаг', 'шага', 'шагов']),
    },
};
